import types from '../mutation-types';
import authAPI from '../../api/auth';

import { setUser, clearCookiesOnLogout } from '../utils/api';
import { accountSignatureSettings } from '../../helper/signatureHelper';

let uiSettingsWriteQueue = Promise.resolve();

const initialState = {
  currentUser: {
    id: null,
    account_id: null,
    accounts: [],
    email: null,
    name: null,
    message_signature: '',
  },
  uiFlags: {
    isFetching: true,
  },
};

// getters
export const getters = {
  isLoggedIn($state) {
    return !!$state.currentUser.id;
  },

  getCurrentUserID($state) {
    return $state.currentUser.id;
  },

  getUISettings($state) {
    return $state.currentUser.ui_settings || {};
  },

  getAuthUIFlags($state) {
    return $state.uiFlags;
  },

  getCurrentUserAvailability($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      (account) => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.availability;
  },

  getCurrentUserAutoOffline($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      (account) => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.auto_offline;
  },

  getCurrentAccountId(_, __, rootState) {
    if (rootState.route.params && rootState.route.params.accountId) {
      return Number(rootState.route.params.accountId);
    }
    return null;
  },

  getCurrentRole($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      (account) => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.role;
  },

  getCurrentUser($state) {
    return $state.currentUser;
  },

  // Firma e interruttore sono per account: quelli dell'account corrente.
  getMessageSignature($state, $getters) {
    return accountSignatureSettings(
      $state.currentUser,
      $getters.getCurrentAccountId
    ).signature;
  },

  getSignatureEnabled($state, $getters) {
    return accountSignatureSettings(
      $state.currentUser,
      $getters.getCurrentAccountId
    ).enabled;
  },

  getCurrentAccount($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      (account) => account.id === $getters.getCurrentAccountId
    );
    return currentAccount || {};
  },

  getUserAccounts($state) {
    const { accounts = [] } = $state.currentUser;
    return accounts;
  },

  userId: (state) => state.currentUser.id, // Assicurati che questo getter sia corretto
};

// actions
export const actions = {
  async validityCheck(context) {
    try {
      const response = await authAPI.validityCheck();
      const currentUser = response.data.payload.data;
      setUser(currentUser);
      context.commit(types.SET_CURRENT_USER, currentUser);
      return currentUser;
    } catch (error) {
      if (error?.response?.status === 401) {
        clearCookiesOnLogout();
      }
      throw error;
    }
  },

  async setUser({ commit, dispatch }) {
    commit(types.SET_CURRENT_USER_UI_FLAGS, { isFetching: true });
    try {
      if (authAPI.hasAuthCookie()) {
        const user = await dispatch('validityCheck');
        commit(types.SET_CURRENT_USER, user);
      } else {
        commit(types.CLEAR_USER);
      }
    } catch (error) {
      commit(types.CLEAR_USER);
    } finally {
      commit(types.SET_CURRENT_USER_UI_FLAGS, { isFetching: false });
    }
  },

  logout({ commit }) {
    commit(types.CLEAR_USER);
  },

  updateProfile: async ({ commit }, params) => {
    const response = await authAPI.profileUpdate(params);
    commit(types.SET_CURRENT_USER, response.data);
    return response.data;
  },

  // La firma si scrive sulla membership dell'account corrente: la risposta
  // riporta accounts[] aggiornato, da cui leggono i getter qui sopra.
  updateAccountSignature: async ({ commit, getters: $getters }, signature) => {
    const response = await authAPI.updateAccountSignature({
      accountId: $getters.getCurrentAccountId,
      ...signature,
    });
    commit(types.SET_CURRENT_USER, response.data);
    return response.data;
  },

  updateSignature: ({ dispatch }, signature) =>
    dispatch('updateAccountSignature', { message_signature: signature }),

  updateSignatureEnabled: ({ dispatch }, enabled) =>
    dispatch('updateAccountSignature', { signature_enabled: Boolean(enabled) }),

  deleteAvatar: async () => {
    try {
      await authAPI.deleteAvatar();
    } catch (error) {
      // Ignore error
    }
  },

  updateUISettings: ({ commit, state: $state }, params = {}) => {
    const runUpdate = async () => {
      const previousUISettings = { ...($state.currentUser.ui_settings || {}) };
      const uiSettingsPatch = params.uiSettingsPatch || params.uiSettings || {};
      const uiSettings = params.replace
        ? { ...uiSettingsPatch }
        : { ...previousUISettings, ...uiSettingsPatch };

      (params.removeUISettingKeys || []).forEach((key) => {
        delete uiSettings[key];
      });

      commit(types.SET_CURRENT_USER_UI_SETTINGS, {
        uiSettings,
        replace: true,
      });

      try {
        const response = await authAPI.updateUISettings({ uiSettings });
        commit(types.SET_CURRENT_USER, response.data);
        return response.data;
      } catch (error) {
        commit(types.SET_CURRENT_USER_UI_SETTINGS, {
          uiSettings: previousUISettings,
          replace: true,
        });
        if (params.rejectOnError) throw error;
        return null;
      }
    };

    const queuedUpdate = uiSettingsWriteQueue.then(runUpdate, runUpdate);
    uiSettingsWriteQueue = queuedUpdate.catch(() => null);
    return queuedUpdate;
  },

  updateAvailability: async ({ commit, dispatch }, params) => {
    try {
      const response = await authAPI.updateAvailability(params);
      const userData = response.data;
      const { id } = userData;
      commit(types.SET_CURRENT_USER, response.data);
      dispatch('agents/updateSingleAgentPresence', {
        id,
        availabilityStatus: params.availability,
      });
    } catch (error) {
      // Ignore error
    }
  },

  updateAutoOffline: async ({ commit }, { accountId, autoOffline }) => {
    try {
      const response = await authAPI.updateAutoOffline(accountId, autoOffline);
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      // Ignore error
    }
  },

  // currentUser.account_id e' l'ultimo account usato, non quello a video: con
  // piu' account la presenza va scritta sull'account da cui arriva (il
  // presence.update porta il suo account_id), altrimenti su quello aperto.
  setCurrentUserAvailability(
    { commit, state: $state, getters: $getters },
    { users = {}, accountId } = {}
  ) {
    const availability = users[$state.currentUser.id];
    const targetAccountId = Number(accountId) || $getters.getCurrentAccountId;
    if (availability && targetAccountId) {
      commit(types.SET_CURRENT_USER_AVAILABILITY, {
        accountId: targetAccountId,
        availability,
      });
    }
  },

  setActiveAccount: async (_, { accountId }) => {
    try {
      await authAPI.setActiveAccount({ accountId });
    } catch (error) {
      // Ignore error
    }
  },

  resendConfirmation: async () => {
    try {
      await authAPI.resendConfirmation();
    } catch (error) {
      // Ignore error
    }
  },
};

// mutations
export const mutations = {
  [types.SET_CURRENT_USER_AVAILABILITY](_state, { accountId, availability }) {
    const accounts = _state.currentUser.accounts.map((account) => {
      if (account.id === Number(accountId)) {
        return { ...account, availability, availability_status: availability };
      }
      return account;
    });
    _state.currentUser = {
      ..._state.currentUser,
      accounts,
    };
  },
  [types.CLEAR_USER](_state) {
    _state.currentUser = { ...initialState.currentUser };
  },
  [types.SET_CURRENT_USER](_state, currentUser) {
    _state.currentUser = {
      ...initialState.currentUser,
      ...currentUser,
      message_signature: currentUser.message_signature || '',
    };
  },
  [types.SET_CURRENT_USER_UI_SETTINGS](
    _state,
    { uiSettings, replace = false }
  ) {
    _state.currentUser = {
      ..._state.currentUser,
      ui_settings: replace
        ? { ...uiSettings }
        : {
            ..._state.currentUser.ui_settings,
            ...uiSettings,
          },
    };
  },

  [types.SET_CURRENT_USER_UI_FLAGS](_state, { isFetching }) {
    _state.uiFlags = { isFetching };
  },
};

export default {
  state: initialState,
  getters,
  actions,
  mutations,
};
